import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ShoppingCart,
  Infinity as InfinityIcon,
  Plus,
  CheckCircle2,
  Loader2,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { FierroIcon } from '@/components/icons/FierroIcon';
import { useBuyPlan } from '@/features/billing/useBuyPlan';
import { useCulqi } from '@/features/billing/useCulqi';
import {
  type BillingMe,
  type Plan,
  type Purchase,
  type TokenTransaction,
  formatPrice,
} from '@/features/billing/types';
import { api } from '@/lib/api';
import { useAuthStore } from '@/stores/authStore';
import { useCartStore } from '@/stores/cartStore';
import { useCartUiStore } from '@/stores/cartUiStore';

/**
 * /app/billing — the user-facing billing dashboard.
 *
 * Sections:
 * 1. Balance card (current fierros + subscription status).
 * 2. Plans grid — "Pasos rápidos" (add to cart, instant 1-click buy).
 * 3. Cart panel — review + checkout (sequential Culqi charges per item).
 * 4. Transaction history — last 50 ledger rows.
 */
export function BillingPage() {
  const patchBilling = useAuthStore((s) => s.patchBilling);

  const meQuery = useQuery({
    queryKey: ['billing', 'me'],
    queryFn: async () => (await api.get<BillingMe>('/api/billing/me')).data,
  });

  const plansQuery = useQuery({
    queryKey: ['billing', 'plans'],
    queryFn: async () => (await api.get<Plan[]>('/api/billing/plans')).data,
  });

  const txQuery = useQuery({
    queryKey: ['billing', 'transactions'],
    queryFn: async () =>
      (await api.get<TokenTransaction[]>('/api/billing/transactions?limit=50')).data,
  });

  const purchasesQuery = useQuery({
    queryKey: ['billing', 'purchases'],
    queryFn: async () =>
      (await api.get<Purchase[]>('/api/billing/purchases?limit=50')).data,
  });

  const items = useCartStore((s) => s.items);
  const openCart = useCartUiStore((s) => s.setOpen);

  const { ready: culqiReady } = useCulqi();
  const buyPlan = useBuyPlan();
  const [successPlan, setSuccessPlan] = useState<string | null>(null);

  // Sync the cached balance to the store as soon as the page loads.
  useEffect(() => {
    if (meQuery.data) {
      patchBilling({
        tokens_balance: meQuery.data.tokens_balance,
        subscription_status: meQuery.data.subscription_status,
        subscription_plan_id: meQuery.data.subscription_plan_id,
        subscription_expires_at: meQuery.data.subscription_expires_at,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meQuery.data?.tokens_balance, meQuery.data?.subscription_expires_at]);

  return (
    <div className="px-8 py-8">
      <div className="mb-8">
        <div className="text-[11px] uppercase tracking-[0.18em]" style={{ color: 'var(--text-muted)' }}>
          Facturación
        </div>
        <h1 className="mb-1 text-3xl">
          <span style={{ color: 'var(--accent)' }}>Fierros</span> y planes
        </h1>
        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
          Recarga tu saldo de exportaciones o activa una suscripción ilimitada.
        </p>
      </div>

      <BalanceCard me={meQuery.data} />

      <section className="mt-10">
        <h2 className="mb-4 text-lg" style={{ fontFamily: 'var(--font-display)', fontWeight: 700 }}>
          Planes disponibles
        </h2>
        {plansQuery.isLoading ? (
          <div style={{ color: 'var(--text-muted)' }}>Cargando planes…</div>
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
            {plansQuery.data?.map((plan) => (
              <PlanCard
                key={plan.id}
                plan={plan}
                busy={buyPlan.busyPlanId === plan.id}
                ready={culqiReady}
                onBuy={async () => {
                  const result = await buyPlan.buy(plan);
                  if (result) setSuccessPlan(plan.name);
                }}
              />
            ))}
          </div>
        )}
        {buyPlan.error && (
          <div
            className="mt-3 rounded-[var(--radius-sm)] border p-2 text-[12.5px]"
            style={{ background: 'rgba(220,38,38,0.08)', borderColor: 'rgba(220,38,38,0.4)', color: '#fca5a5' }}
          >
            {buyPlan.error}
          </div>
        )}
        {successPlan && (
          <div
            className="mt-3 flex items-center gap-2 rounded-[var(--radius-sm)] border p-2 text-[12.5px]"
            style={{ background: 'rgba(34,197,94,0.08)', borderColor: 'rgba(34,197,94,0.4)', color: '#86efac' }}
          >
            <CheckCircle2 size={14} strokeWidth={1.8} />
            <span>
              ¡Listo! <strong>{successPlan}</strong> activado. Refrescamos tu saldo automáticamente.
            </span>
            <button
              type="button"
              className="ml-auto rounded-[var(--radius-sm)] px-2 py-0.5 text-[11px] underline-offset-2 hover:underline"
              onClick={() => setSuccessPlan(null)}
            >
              Cerrar
            </button>
          </div>
        )}
      </section>

      {items.length > 0 && (
        <section className="mt-8">
          {/* Slim summary banner — full cart UX lives in the right-side drawer
              (opened by the CartButton in the TopBar, or by this CTA). */}
          <button
            type="button"
            onClick={() => openCart(true)}
            className="flex w-full items-center justify-between gap-3 rounded-[var(--radius-md)] border p-4 text-left transition-colors hover:bg-[var(--surface-2)]"
            style={{ background: 'var(--surface-1)', borderColor: 'var(--accent)' }}
          >
            <div className="flex items-center gap-3">
              <span
                className="flex h-9 w-9 items-center justify-center rounded-[var(--radius)]"
                style={{ background: 'var(--accent)', color: 'var(--surface-base)' }}
              >
                <ShoppingCart size={16} strokeWidth={1.8} />
              </span>
              <div>
                <div className="text-sm" style={{ color: 'var(--text-primary)' }}>
                  Tienes {items.reduce((a, it) => a + it.quantity, 0)}{' '}
                  {items.reduce((a, it) => a + it.quantity, 0) === 1 ? 'producto' : 'productos'} en
                  el carrito
                </div>
                <div className="text-[12px]" style={{ color: 'var(--text-muted)' }}>
                  Abre el carrito para revisar, ajustar cantidades y pagar.
                </div>
              </div>
            </div>
            <span className="text-[12.5px] font-medium" style={{ color: 'var(--accent)' }}>
              Abrir carrito →
            </span>
          </button>
        </section>
      )}

      <section className="mt-10">
        <h2 className="mb-4 text-lg" style={{ fontFamily: 'var(--font-display)', fontWeight: 700 }}>
          Historial de compras
        </h2>
        <PurchaseList purchases={purchasesQuery.data ?? []} />
      </section>

      <section className="mt-10">
        <div className="mb-4 flex items-baseline justify-between">
          <h2 className="text-lg" style={{ fontFamily: 'var(--font-display)', fontWeight: 700 }}>
            Historial de movimientos
          </h2>
          <span className="text-[11px]" style={{ color: 'var(--text-muted)' }}>
            Cada exportación, recarga o bono mensual
          </span>
        </div>
        <TxList transactions={txQuery.data ?? []} />
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Subcomponents
// ---------------------------------------------------------------------------

function BalanceCard({ me }: { me: BillingMe | undefined }) {
  const sub = !!me?.subscription_active;
  return (
    <div
      className="grid grid-cols-1 gap-4 rounded-[var(--radius-md)] border p-6 md:grid-cols-3"
      style={{ background: 'var(--surface-1)', borderColor: 'var(--border-default)' }}
    >
      <Metric
        label="Saldo de fierros"
        value={
          sub ? (
            <span className="inline-flex items-center gap-1" style={{ color: 'var(--accent)' }}>
              <InfinityIcon size={22} strokeWidth={1.8} /> ilimitado
            </span>
          ) : (
            <span className="inline-flex items-center gap-2">
              <FierroIcon size={20} /> {me?.tokens_balance ?? 0}
            </span>
          )
        }
        hint={sub ? 'Mientras tu suscripción esté activa' : 'Cada exportación cuesta 1–2 fierros'}
      />
      <Metric
        label="Estado de suscripción"
        value={statusLabel(me?.subscription_status)}
        hint={sub ? me?.subscription_plan_slug ?? 'plan activo' : 'sin plan activo'}
      />
      <Metric
        label="Vence"
        value={me?.subscription_expires_at?.slice(0, 10) ?? '—'}
        hint={sub ? 'Se desactiva automáticamente' : '—'}
      />
    </div>
  );
}

function statusLabel(s: string | undefined): string {
  switch (s) {
    case 'active':
      return 'Activa';
    case 'cancelled':
      return 'Cancelada';
    case 'expired':
      return 'Expirada';
    case 'none':
    case undefined:
      return 'Sin suscripción';
    default:
      return s;
  }
}

function Metric({ label, value, hint }: { label: string; value: React.ReactNode; hint: string }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
        {label}
      </div>
      <div className="mt-1 font-mono tabular text-2xl" style={{ color: 'var(--text-primary)' }}>
        {value}
      </div>
      <div className="mt-1 text-xs" style={{ color: 'var(--text-muted)' }}>
        {hint}
      </div>
    </div>
  );
}

function PlanCard({
  plan,
  busy,
  ready,
  onBuy,
}: {
  plan: Plan;
  busy: boolean;
  ready: boolean;
  onBuy: () => void;
}) {
  const addToCart = useCartStore((s) => s.add);
  const openCart = useCartUiStore((s) => s.setOpen);
  const isTrial = plan.kind === 'trial';
  // Subscriptions get the strongest CTA — that's the recurring-revenue plan
  // and matches the user's instruction to highlight them.
  const featured = plan.is_subscription;

  return (
    <div
      className="flex flex-col rounded-[var(--radius-md)] border p-5 transition-all"
      style={{
        background: 'var(--surface-1)',
        borderColor: featured ? 'var(--accent)' : 'var(--border-default)',
        boxShadow: featured ? '0 0 0 1px var(--accent)' : 'none',
      }}
    >
      <div className="mb-2 text-[10px] uppercase tracking-[0.14em]" style={{ color: 'var(--text-muted)' }}>
        {plan.is_subscription ? 'Suscripción' : isTrial ? 'Prueba' : 'Pack de fierros'}
      </div>
      <h3 className="text-base" style={{ fontFamily: 'var(--font-display)', fontWeight: 700 }}>
        {plan.name}
      </h3>
      <p className="mt-1 text-[12.5px]" style={{ color: 'var(--text-secondary)', lineHeight: 1.55 }}>
        {plan.description}
      </p>
      <div className="mt-3 flex items-baseline gap-2">
        <span className="font-mono tabular text-2xl" style={{ color: 'var(--text-primary)' }}>
          {isTrial ? 'Gratis' : formatPrice(plan.price_cents, plan.currency)}
        </span>
        {plan.is_subscription && (
          <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
            / {plan.duration_days === 365 ? 'año' : `${plan.duration_days} días`}
          </span>
        )}
      </div>
      {plan.tokens > 0 && (
        <div className="mt-2 flex items-center gap-1 text-[12px]" style={{ color: 'var(--accent)' }}>
          <FierroIcon size={12} /> {plan.tokens} fierros incluidos
        </div>
      )}
      <div className="mt-auto flex flex-col gap-2 pt-4">
        {isTrial ? (
          <Button variant="ghost" size="sm" disabled>
            Solo al registrarse
          </Button>
        ) : (
          <>
            {/* Primary CTA — add to cart and slide the drawer open so the
                user can confirm + pay. Matches the public /planes flow. */}
            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                addToCart(plan);
                openCart(true);
              }}
            >
              <Plus size={14} strokeWidth={2} />
              Añadir al carrito
            </Button>
            {/* Secondary — single-click impulse buy that skips the cart. */}
            <button
              type="button"
              onClick={onBuy}
              disabled={busy || !ready}
              className="text-[11px] underline-offset-2 hover:underline disabled:cursor-not-allowed disabled:opacity-50"
              style={{ color: 'var(--text-muted)' }}
              title={!ready ? 'Cargando procesador de pagos…' : `Comprar 1 ahora`}
            >
              {busy ? (
                <Loader2 size={11} className="-mt-0.5 mr-1 inline-block animate-spin" />
              ) : (
                <ShoppingCart size={10} strokeWidth={1.8} className="-mt-0.5 mr-1 inline-block" />
              )}
              {busy ? 'Procesando…' : 'o comprar ahora'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function TxList({ transactions }: { transactions: TokenTransaction[] }) {
  if (transactions.length === 0) {
    return (
      <div
        className="rounded-[var(--radius-md)] border p-5 text-sm"
        style={{
          background: 'var(--surface-1)',
          borderColor: 'var(--border-default)',
          color: 'var(--text-muted)',
        }}
      >
        Aún no hay movimientos. Tu bono de bienvenida aparecerá aquí.
      </div>
    );
  }
  return (
    <div
      className="overflow-hidden rounded-[var(--radius-md)] border"
      style={{ background: 'var(--surface-1)', borderColor: 'var(--border-default)' }}
    >
      <table className="w-full text-[13px]">
        <thead>
          <tr className="text-[11px] uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
            <th className="px-4 py-2 text-left">Fecha</th>
            <th className="px-4 py-2 text-left">Razón</th>
            <th className="px-4 py-2 text-right">Δ</th>
            <th className="px-4 py-2 text-right">Saldo</th>
          </tr>
        </thead>
        <tbody>
          {transactions.map((tx) => (
            <tr
              key={tx.id}
              className="border-t"
              style={{ borderColor: 'var(--border-subtle)' }}
            >
              <td className="px-4 py-2 font-mono tabular text-[12px]" style={{ color: 'var(--text-muted)' }}>
                {tx.created_at.slice(0, 19).replace('T', ' ')}
              </td>
              <td className="px-4 py-2">
                <div>{tx.note || tx.reason}</div>
                <div className="text-[11px]" style={{ color: 'var(--text-muted)' }}>
                  {tx.reason}
                </div>
              </td>
              <td
                className="px-4 py-2 text-right font-mono tabular"
                style={{ color: tx.delta >= 0 ? 'var(--accent)' : '#fca5a5' }}
              >
                {tx.delta >= 0 ? `+${tx.delta}` : tx.delta}
              </td>
              <td className="px-4 py-2 text-right font-mono tabular">{tx.balance_after}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Receipt-style table of Culqi purchases. Different from the token-ledger
 * table above: shows real money paid, the plan, status (paid/pending/
 * refunded) and the Culqi charge id so the user can quote it to support.
 */
function PurchaseList({ purchases }: { purchases: Purchase[] }) {
  if (purchases.length === 0) {
    return (
      <div
        className="rounded-[var(--radius-md)] border p-5 text-sm"
        style={{
          background: 'var(--surface-1)',
          borderColor: 'var(--border-default)',
          color: 'var(--text-muted)',
        }}
      >
        Aún no has hecho ninguna compra. Tus pagos de Culqi aparecerán aquí.
      </div>
    );
  }
  return (
    <div
      className="overflow-hidden rounded-[var(--radius-md)] border"
      style={{ background: 'var(--surface-1)', borderColor: 'var(--border-default)' }}
    >
      <table className="w-full text-[13px]">
        <thead>
          <tr className="text-[11px] uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
            <th className="px-4 py-2 text-left">Fecha</th>
            <th className="px-4 py-2 text-left">Plan</th>
            <th className="px-4 py-2 text-right">Monto</th>
            <th className="px-4 py-2 text-left">Estado</th>
            <th className="px-4 py-2 text-left">Culqi ID</th>
          </tr>
        </thead>
        <tbody>
          {purchases.map((p) => (
            <tr key={p.id} className="border-t" style={{ borderColor: 'var(--border-subtle)' }}>
              <td className="px-4 py-2 font-mono tabular text-[12px]" style={{ color: 'var(--text-muted)' }}>
                {p.created_at.slice(0, 19).replace('T', ' ')}
              </td>
              <td className="px-4 py-2">
                <div>{p.plan_name}</div>
                <div className="text-[11px]" style={{ color: 'var(--text-muted)' }}>
                  {p.plan_slug}
                </div>
              </td>
              <td className="px-4 py-2 text-right font-mono tabular">
                {formatPrice(p.amount_cents, p.currency)}
              </td>
              <td className="px-4 py-2">
                <PurchaseStatusPill status={p.status} />
              </td>
              <td className="px-4 py-2 font-mono tabular text-[10.5px]" style={{ color: 'var(--text-muted)' }}>
                {p.culqi_charge_id}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PurchaseStatusPill({ status }: { status: string }) {
  // Map the Culqi-flavored statuses to colors. Anything unrecognized
  // falls back to a neutral pill so we don't crash on a new status the
  // backend may add later (e.g. "chargeback").
  const config: Record<string, { label: string; bg: string; fg: string }> = {
    paid: { label: 'Pagado', bg: 'rgba(34,197,94,0.12)', fg: '#86efac' },
    pending: { label: 'Pendiente', bg: 'rgba(245,158,11,0.14)', fg: 'var(--accent)' },
    failed: { label: 'Fallido', bg: 'rgba(220,38,38,0.12)', fg: '#fca5a5' },
    refunded: { label: 'Reembolsado', bg: 'rgba(148,163,184,0.14)', fg: 'var(--text-muted)' },
  };
  const c = config[status] ?? {
    label: status,
    bg: 'var(--surface-2)',
    fg: 'var(--text-secondary)',
  };
  return (
    <span
      className="inline-flex items-center rounded-[var(--radius-pill)] px-2 py-0.5 text-[10.5px]"
      style={{ background: c.bg, color: c.fg }}
    >
      {c.label}
    </span>
  );
}
